import * as Yup from "yup";
import { FormGroup } from "../../../components/form";
import { LGA } from "../../utils/LGA";
import { maxDob, minDob } from "../../utils/childDobRange";
import { screeningAdvisoryText } from "./screeningAdvisoryText";
import {
  showAdvisoryTextSectionF,
  showAdvisoryTextSectionG,
  showAdvisoryTextSectionH,
  showAdvisoryTextSectionI,
  showAdvisoryTextSectionP,
  showAdvisoryTextSectionR,
} from "./formValidation/kindergartenAdvisoryValidation";

export const kindergartenScreeningQuestionsForm = (navigate: (values: any) => void) => {
  const form: FormGroup = {
    type: "formGroup",
    classNameOpt: "screening-form",
    fields: [
      {
        type: "formGroup",
        key: "applicationBase",
        fields: [
          {
            type: "field",
            key: "$type",
            fieldType: "value",
            defaultValue: "kis.logic.DTO.ApplicationBaseCurrentDTO, kis.logic",
          },

          {
            type: "formGroup",
            key: "screeningQuestions",
            fields: [
              {
                type: "field",
                key: "$type",
                fieldType: "value",
                defaultValue: "kis.logic.DTO.ApplicationValue.ScreeningQuestionsDTO, kis.logic",
              },
              {
                type: "field",
                fieldType: "value",
                key: "showSubmit",
                calculatedValue: (context, values) =>
                  (showAdvisoryTextSectionF(context, values) ||
                    showAdvisoryTextSectionR(context, values) ||
                    showAdvisoryTextSectionH(context, values) ||
                    showAdvisoryTextSectionI(context, values)) &&
                  !(showAdvisoryTextSectionG(context, values) || showAdvisoryTextSectionP(context, values)),
              },
              {
                type: "field",
                fieldType: "radio",
                key: "kinderServiceFunded",
                label:
                  "Is the kinder service funded by the Victorian government, or is it currently undergoing accreditation to become a funded kinder service?",
                validation: Yup.string().trim().required("A response is required"),
                props: {
                  isRequired: true,
                  options: [
                    {
                      label: "Yes",
                      value: 1,
                    },
                    {
                      label: "No",
                      value: 2,
                    },
                  ],
                },
              },
              {
                type: "field",
                key: "advisory1",
                fieldType: "advisory",
                hideExpression: (context, values, model) => model?.kinderServiceFunded != 2,
                props: {
                  type: "warning",
                  Body: () => (
                    <p className="list-none pl-2 m-0">
                      Please note that only kindergarten services that are funded by the Victorian government are eligible for support via the KIS
                      program. Kindergarten services currently undergoing accreditation may submit an application to receive a provisional outcome.
                      Based on your response, as the kindergarten service is not funded by the Victorian government or currently undergoing
                      accreditation, an application cannot be submitted under the KIS program. You can find out more about kindergarten funding{" "}
                      <a href="http://www.vic.gov.au/kindergarten-funding" target="_blank" title="Link opens in a new tab" rel="noreferrer">
                        here
                      </a>
                      . <i aria-hidden="true" className="pi pi-external-link"></i>
                    </p>
                  ),
                },
              },
              {
                type: "formGroup",
                key: "fundedKinder",
                hideExpression: (context, values, model) => model?.kinderServiceFunded != 1,
                fields: [
                  {
                    type: "field",
                    fieldType: "radio",
                    key: "parentGuardianConsent",
                    label: "Please confirm that a parent/carer/guardian of the child has been involved in and consents to the application",
                    validation: Yup.string().trim().required("A response is required"),
                    props: {
                      isRequired: true,
                      options: [
                        {
                          label: "Yes",
                          value: 1,
                        },
                        {
                          label: "No",
                          value: 2,
                        },
                      ],
                    },
                  },
                  {
                    type: "field",
                    key: "advisory2",
                    fieldType: "advisory",
                    props: {
                      Body: () => (
                        <p className="m-0">
                          In order to finalise the application, you will be later required to upload evidence of parent/carer consent in the format
                          requested. Parent/carer consent needs to be recorded using the{" "}
                          <a
                            href="http://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-privacy-notice-and-consent-form.pdf"
                            target="_blank"
                            title="Link opens in a new tab"
                            rel="noreferrer">
                            KIS privacy notice and consent form
                          </a>
                          . <i aria-hidden="true" className="pi pi-external-link"></i>
                        </p>
                      ),
                      type: "fieldadv",
                    },
                  },
                  {
                    type: "field",
                    key: "advisory3",
                    fieldType: "advisory",
                    props: {
                      type: "warning",
                    },
                    hideExpression: (context, values, model) => model.parentGuardianConsent != 2,
                    label:
                      "An application cannot be submitted under the KIS program without the consent of the child's parent/carer/guardian. You cannot continue until parent/carer/guardian consent is obtained.",
                  },
                  {
                    type: "formGroup",
                    key: "consentYes",
                    hideExpression: (context, values, model) => model?.parentGuardianConsent != 1,
                    fields: [
                      {
                        type: "field",
                        fieldType: "radio",
                        key: "screeningApplicationType",
                        label: "What type of application is this?",
                        validation: Yup.string().trim().required("A response is required"),
                        props: {
                          isRequired: true,
                          options: [
                            {
                              label: "New",
                              value: 1,
                            },
                            {
                              label: "Appeal",
                              value: 2,
                            },
                            {
                              label: "Transfer",
                              value: 3,
                            },
                          ],
                        },
                      },
                      {
                        type: "field",
                        key: "advisory4",
                        fieldType: "advisory",
                        props: {
                          type: "fieldadv",
                        },

                        label: "A transfer application should be submitted by the new kindergarten service that the child has moved to",
                      },
                      {
                        type: "field",
                        key: "advisory5",
                        fieldType: "advisory",
                        hideExpression: (context, values, model) => model.screeningApplicationType != 2,
                        props: {
                          type: "warning",
                          Body: () => (
                            <div className="list-none pl-2">
                              <p className="mb-2">
                                To initiate an appeal, please open the child's previous application and navigate to the{" "}
                                <strong>Appeal Request</strong> section found at the bottom of the side panel. You will then need to click{" "}
                                <strong>Edit</strong> on this section, provide a brief reason for the appeal, and <strong>Save</strong> the section.
                              </p>
                              <p className="mb-2">
                                This will create a new appeal application, which can be identified by the title <strong>Appeal</strong> at the top of
                                the application once opened.
                              </p>
                              <p className="mb-2">
                                All information from your previous application will transfer to the appeal,{" "}
                                <u>allowing you to edit any sections you would like to provide further information.</u>
                              </p>
                              <ul className="pl-2 m-0 indentation">
                                The following is also required when submitting an appeal:
                                <li className="ml-5">A new signed privacy and consent form</li>
                                <li className="ml-5">A current KIS plan if under the Disability stream (dated within 3 months of submission)</li>
                              </ul>
                              <p className="my-2">
                                Once you have finished editing the appeal please ensure you <strong>submit</strong> the appeal application.
                              </p>
                              <p className="mb-2">
                                Suggestions for what could be provided in an appeal were included in the outcome email you received. For assistance
                                with preparing the appeal, including what new information should be provided, you should contact your KIS provider as
                                they are best placed to support you in preparing your appeal.
                              </p>
                            </div>
                          ),
                        },
                        label: "",
                      },
                      {
                        type: "field",
                        key: "advisory6",
                        fieldType: "advisory",
                        hideExpression: (context, values, model) => model.screeningApplicationType != 3,
                        props: {
                          type: "warning",
                          Body: () => (
                            <p className="list-none pl-2 m-0">
                              To lodge a transfer application, please contact the KIS support team, via{" "}
                              <a href="mailto:support@KISapplications.com.au">support@KISapplications.com.au</a> or 1300 564 149. You will need to
                              provide: <br />
                              <li>- Your name and the email associated with your KIS portal account</li>
                              <li>- The name of your kindergarten service</li>
                              <li>- The name of the child</li>
                              <li>- The name of their previous kindergarten. </li>
                              <br />
                              Our support team will work with you to set up the transfer application, so the original and transfer applications are
                              linked in the KIS portal and the assessment team have access to all the required information. Please note you will not
                              be able to see the original application.
                            </p>
                          ),
                        },
                        label: "",
                      },
                      {
                        type: "field",
                        fieldType: "radio",
                        key: "applicationCategory",
                        label: "What category is the application being made under?",
                        validation: Yup.number().required("A response is required"),
                        hideExpression: (context, values, model) => model.screeningApplicationType != 1,
                        props: {
                          isRequired: true,
                          options: [
                            {
                              label: "Complex Medical Needs",
                              value: 3,
                            },
                            {
                              label: "Disability (including developmental delay)",
                              value: 1,
                            },
                            {
                              label: "Both Complex Medical Needs and Disability (including developmental delay)",
                              value: 2,
                            },
                          ],
                        },
                      },
                      {
                        type: "field",
                        key: "advisory7",
                        fieldType: "advisory",
                        hideExpression: (context, values, model) => model.screeningApplicationType != 1,
                        props: {
                          type: "fieldadv",
                          Body: () => (
                            <div>
                              To read more about the different application types, please refer to the{" "}
                              <a
                                href="http://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-Program-–-Guidelines-for-the-Disability-and-Complex-Medical-Needs-streams.pdf"
                                target="_blank"
                                title="Link opens in a new tab"
                                rel="noreferrer">
                                KIS program guidelines
                              </a>{" "}
                              <i aria-hidden="true" className="pi pi-external-link"></i>
                            </div>
                          ),
                        },
                      },

                      {
                        type: "field",
                        fieldType: "radio",
                        key: "complexMedicalNeedsCriteria",
                        validation: Yup.number().required("A response is required"),
                        hideExpression: (context, values, model) =>
                          model.screeningApplicationType != 1 || (model.applicationCategory != 3 && model.applicationCategory != 2),
                        props: {
                          Body: () => (
                            <label className="mt-2">
                              <p>
                                To apply under the Complex Medical Needs criteria, a child must have a diagnosis that requires individualised health
                                support/s for their complex medical need/s (this does not include asthma, allergy or anaphylaxis) AND must be unable
                                to access a funded kindergarten program without provision of a high level of additional support for individual health
                                care needs.
                              </p>
                              <p className="isRequired">Please select the type of evidence you can upload:</p>
                            </label>
                          ),
                          isRequired: true,
                          options: [
                            {
                              label: [
                                "Evidence from a medical practitioner that the child has complex medical needs which require a high level of individualised health care support during the kindergarten program",
                                <strong key="1"> AND </strong>,
                                "a risk minimisation plan",
                                <strong key="2"> AND </strong>,
                                "a medical management plan (MMP) prepared or endorsed by a medical practitioner, or medical professionals who are involved in the care/support (e.g. Diabetes Educator/Nurse) of the child in relation to their medical needs. This is to ensure that the support provided to the child and any training undertaken is in line with medical advice.",
                              ],
                              value: 1,
                            },
                            {
                              label: "I can't upload all of the above documents",
                              value: 2,
                            },
                          ],
                        },
                      },
                      {
                        type: "field",
                        key: "advisory8",
                        fieldType: "advisory",
                        hideExpression: (context, values, model) =>
                          model.screeningApplicationType != 1 || (model.applicationCategory != 3 && model.applicationCategory != 2),
                        props: {
                          type: "warning",
                          Body: () => (
                            <>
                              <p className="list-none pl-2 m-0">
                                Please note that applications for support under this category should not be submitted where asthma, allergy or
                                anaphylaxis are the only diagnosis. The following list provides examples of procedures for which Complex Medical Needs
                                program funding may be provided:
                                <li>-Oxygen</li>
                                <li>-Tracheostomy care</li>
                                <li>-Suction</li>
                                <li>-Tube feeding</li>
                                <li>-Ileostomy or colostomy management</li>
                                For the purpose of the KIS application, a medical practitioner is a health professional registered under the Health
                                Practitioner Regulation National Law to practice in the medical profession. These professionals include general
                                practitioners, paediatricians, registered psychologists, medical specialists, and other medical professionals trained
                                and qualified to diagnose and treat illnesses, injuries, and diseases. <br /> <br />
                                An MMP should be prepared or endorsed by a medical practitioner, or medical professionals (e.g. Diabetes
                                Educator/Nurse) who are involved in the care/support of the child in relation to their medical needs. This is to
                                ensure that the support provided to the child and any training undertaken is in line with medical advice.
                              </p>
                            </>
                          ),
                        },
                        label: "",
                      },
                      {
                        type: "formGroup",
                        key: "childsDisability",
                        label: "The child's disability means that in the kinder environment they are (select all that apply):",
                        hideExpression: (context, values, model) =>
                          model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
                        test: [
                          "oneSelected",
                          "A response is required",
                          (value) => value.riskOfSeriousInjury || value.restrictedMovement || value.childsDisabilityNone,
                        ],
                        props: {
                          isRequired: true,
                        },
                        fields: [
                          {
                            type: "field",
                            key: "riskOfSeriousInjury",
                            fieldType: "checkbox",
                            label: "At significant risk of serious injury to themselves and/or others",
                            expressionProps: {
                              disabled: (context, values, model) => model.childsDisabilityNone,
                            },
                            props: {
                              showDisabled: true,
                            },
                          },
                          {
                            type: "field",
                            key: "restrictedMovement",
                            fieldType: "checkbox",
                            label: "Extremely restricted in their capacity for movement",
                            expressionProps: {
                              disabled: (context, values, model) => model.childsDisabilityNone,
                            },
                            props: {
                              showDisabled: true,
                            },
                          },
                          {
                            type: "field",
                            key: "childsDisabilityNone",
                            fieldType: "checkbox",
                            label: "None of the above",
                            expressionProps: {
                              disabled: (context, values, model) => model.restrictedMovement || model.riskOfSeriousInjury,
                            },
                            props: {
                              showDisabled: true,
                            },
                          },
                        ],
                      },
                      {
                        type: "field",
                        fieldType: "radio",
                        key: "childDiagnosedDisability",
                        label: "Is the child:",
                        validation: Yup.number().required("A response is required"),
                        hideExpression: (context, values, model) =>
                          model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
                        props: {
                          isRequired: true,
                          options: [
                            {
                              label: [
                                "Diagnosed with a disability (including developmental delay)",
                                <strong key="1"> AND </strong>,
                                "has ongoing high support needs",
                              ],
                              value: 1,
                            },
                            {
                              label: [
                                "Undergoing assessment for a disability (including developmental delay)",
                                <strong key="2"> AND </strong>,
                                "has ongoing high support needs",
                              ],
                              value: 2,
                            },
                            {
                              label: "None of the above",
                              value: 3,
                            },
                          ],
                        },
                      },
                      {
                        type: "field",
                        fieldType: "radio",
                        key: "typeOfDiagnosedDisabilityEvidence",
                        label:
                          "Please select the type of evidence you can upload to confirm the child's disability diagnosis. Only one type of evidence is required to be uploaded but you can upload other relevant evidence.",
                        validation: Yup.number().required("A response is required"),
                        hideExpression: (context, values, model) =>
                          model.screeningApplicationType != 1 || model.applicationCategory == 3 || model.childDiagnosedDisability != 1,
                        props: {
                          options: [
                            {
                              label:
                                "Evidence from a paediatrician, specialist medical practitioner or registered psychologist, such as a letter or a report that details the disability diagnosis",
                              value: 1,
                            },
                            {
                              label: "A current NDIS plan",
                              value: 2,
                            },
                            {
                              label:
                                "Evidence the child is eligible for Victorian government Early Childhood Intervention Services (ECIS) – Continuity of Support (CoS)-  note this is not support provided through the NDIA",
                              value: 3,
                            },
                            {
                              label: "None of the above",
                              value: 4,
                            },
                          ],
                        },
                      },
                      {
                        type: "field",
                        key: "advisory9",
                        fieldType: "advisory",
                        hideExpression: (context, values, model) =>
                          model.applicationCategory == 3 ||
                          model.screeningApplicationType != 1 ||
                          model.childDiagnosedDisability != 1 ||
                          model.typeOfDiagnosedDisabilityEvidence != 1,

                        props: {
                          type: "warning",
                          Body: () => (
                            <p className="list-none pl-2 m-0">
                              Please note that evidence from allied health practitioners (e.g. occupational therapists, speech pathologists etc)
                              cannot be used to confirm a medical diagnosis for the KIS program.
                              <br />
                              Evidence documents must meet the requirements outlined in the KIS program guidelines.
                            </p>
                          ),
                        },
                        label: "",
                      },
                      {
                        type: "field",
                        key: "advisory10",
                        fieldType: "advisory",
                        hideExpression: (context, values, model) =>
                          model.screeningApplicationType != 1 ||
                          model.applicationCategory == 3 ||
                          model.childDiagnosedDisability != 1 ||
                          model.typeOfDiagnosedDisabilityEvidence != 2,
                        props: {
                          type: "warning",
                          Body: () => (
                            <p className="list-none pl-2 m-0">
                              The end date of the plan must be after this KIS application is submitted. A rolled over plan can be submitted if other
                              evidence is uploaded to confirm that the plan has been extended, such as a screenshot from the NDIA portal that shows
                              the new end date, the NDIS number and the child's name.
                              <br />
                              Evidence showing that an NDIS application has been lodged is not accepted for the KIS Program and should not be
                              submitted.
                              <br />
                              Evidence documents must meet the requirements outlined in the KIS program guidelines.
                            </p>
                          ),
                        },
                        label: "",
                      },
                      {
                        type: "field",
                        key: "advisory11",
                        fieldType: "advisory",
                        hideExpression: (context, values, model) =>
                          model.screeningApplicationType != 1 ||
                          model.applicationCategory == 3 ||
                          model.childDiagnosedDisability != 1 ||
                          model.typeOfDiagnosedDisabilityEvidence != 3,
                        props: {
                          type: "warning",
                          Body: () => (
                            <p className="list-none pl-2 m-0">
                              The ECIS CoS program applies to children who would otherwise be eligible for NDIS support, but do not meet the residency
                              requirements. If the child has an NDIS plan, do not select this option.
                            </p>
                          ),
                        },
                        label: "",
                      },
                      {
                        type: "field",
                        fieldType: "radio",
                        key: "evidenceUndergoingAssessment",
                        label:
                          "Please select the type of evidence you can upload to confirm that the child is undergoing continuing assessment for disability. Only one evidence option is required to be uploaded but you can upload other relevant evidence.",
                        validation: Yup.number().required("A response is required"),
                        hideExpression: (context, values, model) => model.applicationCategory == 3 || model.childDiagnosedDisability != 2,
                        props: {
                          options: [
                            {
                              label: [
                                "Option 1 An appointment letter for a disability assessment by a paediatrician, specialist medical practitioner or registered psychologist",
                                <strong key="1"> AND </strong>,
                                "Supporting documentation from a medical professional outlining the child’s development and confirming that the child has been referred for assessment of disability",
                              ],
                              value: 1,
                            },
                            {
                              label:
                                "Option 2 Documentation from a paediatrician, specialist medical practitioner or registered psychologist showing that they will have further contact with the child for the purpose of ongoing assessment for a disability",
                              value: 2,
                            },
                            {
                              label: "None of the above",
                              value: 3,
                            },
                          ],
                        },
                      },
                      {
                        type: "field",
                        key: "advisory12",
                        fieldType: "advisory",
                        hideExpression: (context, values, model) => model.applicationCategory == 3 || model.childDiagnosedDisability != 2,
                        props: {
                          type: "warning",
                          Body: () => (
                            <p className="list-none pl-2 m-0">
                              Please note that:
                              <li>
                                -pending appointments for assessment with allied health professionals (e.g. occupational therapist, speech
                                pathologist) cannot be accepted
                              </li>
                              <li>
                                -the documents must show that the appointment is for ongoing assessment for disability, rather than general checkup
                                appointments
                              </li>
                              <li>
                                -if the child has an <strong>NDIS plan</strong> as evidence of disability or developmental delay, select the
                                <i>
                                  Diagnosed with a disability (including developmental delay) <strong>AND</strong> has ongoing high support needs
                                </i>
                                option from the list above
                              </li>
                            </p>
                          ),
                        },
                        label: "",
                      },
                      {
                        type: "field",
                        fieldType: "radio",
                        key: "kisPlanCompleted",
                        label: "Has a KIS plan for the child been completed or updated within the last 3 months?",
                        validation: Yup.number().required("A response is required"),
                        hideExpression: (context, values, model) =>
                          model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
                        props: {
                          isRequired: true,
                          options: [
                            {
                              label: "Yes",
                              value: 1,
                            },
                            {
                              label: "No",
                              value: 2,
                            },
                          ],
                        },
                      },
                      {
                        type: "field",
                        key: "advisory13",
                        fieldType: "advisory",
                        hideExpression: (context, values, model) =>
                          model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
                        props: {
                          type: "fieldadv",
                          Body: () => (
                            <div>
                              {" "}
                              Please note that you will be required to upload a copy of a{" "}
                              <a
                                href="https://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-plan.pdf"
                                target="_blank"
                                title="Link opens in a new tab"
                                rel="noreferrer">
                                KIS plan
                              </a>{" "}
                              <i aria-hidden="true" className="pi pi-external-link"></i> dated within the last 3 months in order to submit an
                              application for support under the disability stream of the KIS program.
                            </div>
                          ),
                        },
                        label: "",
                      },
                      {
                        type: "field",
                        key: "advisory3",
                        fieldType: "advisory",
                        props: {
                          type: "warning",
                        },
                        hideExpression: (context, values, model) => model.parentGuardianConsent != 2,
                        label:
                          "An application cannot be submitted under the KIS program without the consent of the child's parent/carer/guardian. You cannot continue until parent/carer/guardian consent is obtained.",
                      },
                      {
                        type: "formGroup",
                        key: "consentYes",
                        hideExpression: (context, values, model) => model?.parentGuardianConsent != 1,
                        fields: [
                          {
                            type: "field",
                            fieldType: "radio",
                            key: "screeningApplicationType",
                            label: "What type of application is this?",
                            validation: Yup.string().trim().required("A response is required"),
                            props: {
                              isRequired: true,
                              options: [
                                {
                                  label: "New",
                                  value: 1,
                                },
                                {
                                  label: "Appeal",
                                  value: 2,
                                },
                                {
                                  label: "Transfer",
                                  value: 3,
                                },
                              ],
                            },
                          },
                          {
                            type: "field",
                            key: "advisory4",
                            fieldType: "advisory",
                            props: {
                              type: "fieldadv",
                            },

                            label: "A transfer application should be submitted by the new kindergarten service that the child has moved to",
                          },
                          {
                            type: "field",
                            key: "advisory5",
                            fieldType: "advisory",
                            hideExpression: (context, values, model) => model.screeningApplicationType != 3,
                            props: {
                              type: "warning",
                              Body: () => (
                                <p className="list-none pl-2 m-0">
                                  To lodge a transfer application, please contact the KIS support team, via{" "}
                                  <a href="mailto:support@KISapplications.com.au">support@KISapplications.com.au</a> or 1300 564 149. You will need to
                                  provide: <br />
                                  <li>- Your name and the email associated with your KIS portal account</li>
                                  <li>- The name of your kindergarten service</li>
                                  <li>- The name of the child</li>
                                  <li>- The name of their previous kindergarten. </li>
                                  Our support team will work with you to set up the transfer application, so the original and transfer applications
                                  are linked in the KIS portal and the assessment team have access to all the required information. Please note you
                                  will not be able to see the original application.
                                </p>
                              ),
                            },
                            label: "",
                          },
                          {
                            type: "field",
                            fieldType: "radio",
                            key: "appealForLodgedApplication",
                            label: "Is this an appeal for an application that was originally lodged:",
                            validation: Yup.number().required("A response is required"),
                            hideExpression: (context, values, model) => model.screeningApplicationType != 2,
                            props: {
                              isRequired: true,
                              options: [
                                {
                                  label: "Prior to term 4 2025 (i.e. completed using a PDF or Word document and then uploaded to the KIS portal)",
                                  value: 1,
                                },
                                {
                                  label: "During or after term 4 2025 i.e. completed by filling in the online application form in this KIS portal)",
                                  value: 2,
                                },
                              ],
                            },
                          },
                          {
                            type: "field",
                            key: "advisory6",
                            fieldType: "advisory",
                            props: {
                              type: "warning",
                            },
                            hideExpression: (context, values, model) => model.screeningApplicationType != 2 || model.appealForLodgedApplication != 1,
                            label:
                              "As the original application for this appeal was lodged using the previous application form and online submission system, you will need to contact the KIS support team via support@KISapplications.com.au or 1300 564 149 to assist you to lodge this appeal.",
                          },
                          {
                            type: "field",
                            key: "advisory7",
                            fieldType: "advisory",
                            hideExpression: (context, values, model) => model.screeningApplicationType != 2 || model.appealForLodgedApplication != 2,
                            props: {
                              type: "warning",
                              Body: () => (
                                <div className="list-none pl-2">
                                  <p className="mt-0">
                                    To lodge an appeal for an application that already exists in the KIS portal, please log in and navigate to the
                                    original application to begin the appeal process. This means you don't need to provide all of the same information
                                    again.
                                  </p>
                                  <p>
                                    If the original application was not created using your account, please contact the KIS support team, via
                                    support@KISapplications.com.au or 1300 564 149.
                                  </p>
                                </div>
                              ),
                            },
                            label: "",
                          },
                          {
                            type: "field",
                            fieldType: "radio",
                            key: "applicationCategory",
                            label: "What category is the application being made under?",
                            validation: Yup.number().required("A response is required"),
                            hideExpression: (context, values, model) => model.screeningApplicationType != 1,
                            props: {
                              isRequired: true,
                              options: [
                                {
                                  label: "Complex Medical Needs",
                                  value: 3,
                                },
                                {
                                  label: "Disability (including developmental delay)",
                                  value: 1,
                                },
                                {
                                  label: "Both Complex Medical Needs and Disability (including developmental delay)",
                                  value: 2,
                                },
                              ],
                            },
                          },
                          {
                            type: "field",
                            key: "advisory8",
                            fieldType: "advisory",
                            hideExpression: (context, values, model) => model.screeningApplicationType != 1,
                            props: {
                              type: "fieldadv",
                              Body: () => (
                                <div>
                                  To read more about the different application types, please refer to the{" "}
                                  <a
                                    href="http://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-Program-–-Guidelines-for-the-Disability-and-Complex-Medical-Needs-streams.pdf"
                                    target="_blank"
                                    title="Link opens in a new tab"
                                    rel="noreferrer">
                                    KIS program guidelines
                                  </a>{" "}
                                  <i aria-hidden="true" className="pi pi-external-link"></i>
                                </div>
                              ),
                            },
                          },

                          {
                            type: "field",
                            fieldType: "radio",
                            key: "complexMedicalNeedsCriteria",
                            label:
                              "To apply under the Complex Medical Needs criteria, a child must have a diagnosis that requires individualised health support/s for their complex medical need/s (this does not include asthma, allergy or anaphylaxis) AND  must be unable to access a funded kindergarten program without provision of a high level of additional support for individual health care needs. Please select the type of evidence you can upload:",
                            validation: Yup.number().required("A response is required"),
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 || (model.applicationCategory != 3 && model.applicationCategory != 2),
                            props: {
                              isRequired: true,
                              options: [
                                {
                                  label: [
                                    "Evidence from a medical practitioner that the child has complex medical needs which require a high level of individualised health care support during the kindergarten program",
                                    <strong key="1"> AND </strong>,
                                    "a risk minimisation plan",
                                    <strong key="2"> AND </strong>,
                                    "a medical management plan (MMP) prepared or endorsed by a medical practitioner, or medical professionals who are involved in the care/support (e.g. Diabetes Educator/Nurse) of the child in relation to their medical needs. This is to ensure that the support provided to the child and any training undertaken is in line with medical advice.",
                                  ],
                                  value: 1,
                                },
                                {
                                  label: "I can't upload all of the above documents",
                                  value: 2,
                                },
                              ],
                            },
                          },
                          {
                            type: "field",
                            key: "advisory9",
                            fieldType: "advisory",
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 || (model.applicationCategory != 3 && model.applicationCategory != 2),
                            props: {
                              type: "helpertext",
                              Body: () => (
                                <div className="list-none pl-2 m-0">
                                  Please note that applications for support under this category should not be submitted where asthma, allergy or
                                  anaphylaxis are the only diagnosis. The following list provides examples of procedures for which Complex Medical
                                  Needs program funding may be provided:
                                  <li>-Oxygen</li>
                                  <li>-Tracheostomy care</li>
                                  <li>-Suction</li>
                                  <li>-Tube feeding</li>
                                  <li>-Ileostomy or colostomy management</li>
                                  <p className="mt-1 m-0">
                                    {" "}
                                    For the purpose of the KIS application, a medical practitioner is a health professional registered under the
                                    Health Practitioner Regulation National Law to practice in the medical profession. These professionals include
                                    general practitioners, paediatricians, registered psychologists, medical specialists, and other medical
                                    professionals trained and qualified to diagnose and treat illnesses, injuries, and diseases. <br /> <br />
                                    An MMP should be prepared or endorsed by a medical practitioner, or medical professionals (e.g. Diabetes
                                    Educator/Nurse) who are involved in the care/support of the child in relation to their medical needs. This is to
                                    ensure that the support provided to the child and any training undertaken is in line with medical advice.
                                  </p>
                                </div>
                              ),
                            },
                            label: "",
                          },
                          {
                            type: "formGroup",
                            key: "childsDisability",
                            label: "The child's disability means that in the kinder environment they are (select all that apply):",
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
                            test: [
                              "oneSelected",
                              "A response is required",
                              (value) => value.riskOfSeriousInjury || value.restrictedMovement || value.childsDisabilityNone,
                            ],
                            props: {
                              isRequired: true,
                            },
                            fields: [
                              {
                                type: "field",
                                key: "riskOfSeriousInjury",
                                fieldType: "checkbox",
                                label: "At significant risk of serious injury to themselves and/or others",
                                expressionProps: {
                                  disabled: (context, values, model) => model.childsDisabilityNone,
                                },
                                props: {
                                  showDisabled: true,
                                },
                              },
                              {
                                type: "field",
                                key: "restrictedMovement",
                                fieldType: "checkbox",
                                label: "Extremely restricted in their capacity for movement",
                                expressionProps: {
                                  disabled: (context, values, model) => model.childsDisabilityNone,
                                },
                                props: {
                                  showDisabled: true,
                                },
                              },
                              {
                                type: "field",
                                key: "childsDisabilityNone",
                                fieldType: "checkbox",
                                label: "None of the above",
                                expressionProps: {
                                  disabled: (context, values, model) => model.restrictedMovement || model.riskOfSeriousInjury,
                                },
                                props: {
                                  showDisabled: true,
                                },
                              },
                            ],
                          },
                          {
                            type: "field",
                            fieldType: "radio",
                            key: "childDiagnosedDisability",
                            label: "Is the child:",
                            validation: Yup.number().required("A response is required"),
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
                            props: {
                              isRequired: true,
                              options: [
                                {
                                  label: [
                                    "Diagnosed with a disability (including developmental delay)",
                                    <strong key="1"> AND </strong>,
                                    "has ongoing high support needs",
                                  ],
                                  value: 1,
                                },
                                {
                                  label: [
                                    "Undergoing assessment for a disability (including developmental delay)",
                                    <strong key="2"> AND </strong>,
                                    "has ongoing high support needs",
                                  ],
                                  value: 2,
                                },
                                {
                                  label: "None of the above",
                                  value: 3,
                                },
                              ],
                            },
                          },
                          {
                            type: "field",
                            fieldType: "radio",
                            key: "typeOfDiagnosedDisabilityEvidence",
                            label:
                              "Please select the type of evidence you can upload to confirm the child's disability diagnosis. Only one type of evidence is required to be uploaded but you can upload other relevant evidence.",
                            validation: Yup.number().required("A response is required"),
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 || model.applicationCategory == 3 || model.childDiagnosedDisability != 1,
                            props: {
                              options: [
                                {
                                  label:
                                    "Evidence from a paediatrician, specialist medical practitioner or registered psychologist, such as a letter or a report that details the disability diagnosis",
                                  value: 1,
                                },
                                {
                                  label: "A current NDIS plan",
                                  value: 2,
                                },
                                {
                                  label:
                                    "Evidence the child is eligible for Victorian government Early Childhood Intervention Services (ECIS) – Continuity of Support (CoS)-  note this is not support provided through the NDIA",
                                  value: 3,
                                },
                                {
                                  label: "None of the above",
                                  value: 4,
                                },
                              ],
                            },
                          },
                          {
                            type: "field",
                            key: "advisory10",
                            fieldType: "advisory",
                            hideExpression: (context, values, model) =>
                              model.applicationCategory == 3 ||
                              model.screeningApplicationType != 1 ||
                              model.childDiagnosedDisability != 1 ||
                              model.typeOfDiagnosedDisabilityEvidence != 1,

                            props: {
                              type: "warning",
                              Body: () => (
                                <p className="list-none pl-2 m-0">
                                  Please note that evidence from allied health practitioners (e.g. occupational therapists, speech pathologists etc)
                                  cannot be used to confirm a medical diagnosis for the KIS program.
                                  <br />
                                  Evidence documents must meet the requirements outlined in the KIS program guidelines.
                                </p>
                              ),
                            },
                            label: "",
                          },
                          {
                            type: "field",
                            key: "advisory11",
                            fieldType: "advisory",
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 ||
                              model.applicationCategory == 3 ||
                              model.childDiagnosedDisability != 1 ||
                              model.typeOfDiagnosedDisabilityEvidence != 2,
                            props: {
                              type: "warning",
                              Body: () => (
                                <p className="list-none pl-2 m-0">
                                  The end date of the plan must be after this KIS application is submitted. A rolled over plan can be submitted if
                                  other evidence is uploaded to confirm that the plan has been extended, such as a screenshot from the NDIA portal
                                  that shows the new end date, the NDIS number and the child's name.
                                  <br />
                                  Evidence showing that an NDIS application has been lodged is not accepted for the KIS Program and should not be
                                  submitted.
                                  <br />
                                  Evidence documents must meet the requirements outlined in the KIS program guidelines.
                                </p>
                              ),
                            },
                            label: "",
                          },
                          {
                            type: "field",
                            key: "advisory12",
                            fieldType: "advisory",
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 ||
                              model.applicationCategory == 3 ||
                              model.childDiagnosedDisability != 1 ||
                              model.typeOfDiagnosedDisabilityEvidence != 3,
                            props: {
                              type: "warning",
                              Body: () => (
                                <p className="list-none pl-2 m-0">
                                  The ECIS CoS program applies to children who would otherwise be eligible for NDIS support, but do not meet the
                                  residency requirements. If the child has an NDIS plan, do not select this option.
                                </p>
                              ),
                            },
                            label: "",
                          },
                          {
                            type: "field",
                            fieldType: "radio",
                            key: "evidenceUndergoingAssessment",
                            label:
                              "Please select the type of evidence you can upload to confirm that the child is undergoing continuing assessment for disability. Only one evidence option is required to be uploaded but you can upload other relevant evidence.",
                            validation: Yup.number().required("A response is required"),
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 || model.applicationCategory == 3 || model.childDiagnosedDisability != 2,
                            props: {
                              isRequired: true,
                              options: [
                                {
                                  label: [
                                    "Option 1 An appointment letter for a disability assessment by a paediatrician, specialist medical practitioner or registered psychologist",
                                    <strong key="1"> AND </strong>,
                                    "Supporting documentation from a medical professional outlining the child’s development and confirming that the child has been referred for assessment of disability",
                                  ],
                                  value: 1,
                                },
                                {
                                  label:
                                    "Option 2 Documentation from a paediatrician, specialist medical practitioner or registered psychologist showing that they will have further contact with the child for the purpose of ongoing assessment for a disability",
                                  value: 2,
                                },
                                {
                                  label: "None of the above",
                                  value: 3,
                                },
                              ],
                            },
                          },
                          {
                            type: "field",
                            key: "advisory13",
                            fieldType: "advisory",
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 || model.applicationCategory == 3 || model.childDiagnosedDisability != 2,
                            props: {
                              type: "helpertext",
                              Body: () => (
                                <p className="list-none pl-2 m-0">
                                  Please note that:
                                  <li>
                                    -pending appointments for assessment with allied health professionals (e.g. occupational therapist, speech
                                    pathologist) cannot be accepted
                                  </li>
                                  <li>
                                    -the documents must show that the appointment is for ongoing assessment for disability, rather than general
                                    checkup appointments
                                  </li>
                                </p>
                              ),
                            },
                            label: "",
                          },
                          {
                            type: "field",
                            fieldType: "radio",
                            key: "kisPlanCompleted",
                            label: "Has a KIS plan for the child been completed or updated within the last 3 months?",
                            validation: Yup.number().required("A response is required"),
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
                            props: {
                              isRequired: true,
                              options: [
                                {
                                  label: "Yes",
                                  value: 1,
                                },
                                {
                                  label: "No",
                                  value: 2,
                                },
                              ],
                            },
                          },
                          {
                            type: "field",
                            key: "advisory14",
                            fieldType: "advisory",
                            hideExpression: (context, values, model) =>
                              model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
                            props: {
                              type: "fieldadv",
                              Body: () => (
                                <div>
                                  {" "}
                                  Please note that you will be required to upload a copy of a{" "}
                                  <a
                                    href="https://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-plan.pdf"
                                    target="_blank"
                                    title="Link opens in a new tab"
                                    rel="noreferrer">
                                    KIS plan
                                  </a>{" "}
                                  <i aria-hidden="true" className="pi pi-external-link"></i> dated within the last 3 months in order to submit an
                                  application for support under the disability stream of the KIS program.
                                </div>
                              ),
                            },
                            label: "",
                          },
                        ],
                      },
                      ...screeningAdvisoryText,
                    ],
                  },
                ],
              },
            ],
          },
          {
            type: "formGroup",
            heading: "Application Creation",
            key: "child",
            hideExpression: (context, values) => hideSection(values),
            fields: [
              {
                type: "field",
                key: "$type",
                fieldType: "value",
                defaultValue: "kis.logic.DTO.ApplicationValue.ChildDTOs.ChildCurrentDTO, kis.logic",
              },
              {
                type: "field",
                key: "firstName",
                fieldType: "input",
                label: "Child's given name",
                props: {
                  isRequired: true,
                },
                validation: Yup.string()
                  .trim()
                  .required("A response is required") // Basic required validation
                  .matches(RegExp("^(?! )[A-Za-z' -]+(?<! )$"), {
                    message: "Name may only use alphabetic (A-Z) characters and hyphen (-) and apostrophe (')",
                  }),
              },
              {
                type: "field",
                key: "lastName",
                fieldType: "input",
                label: "Child's family name",
                props: {
                  isRequired: true,
                },
                validation: Yup.string()
                  .trim()
                  .required("A response is required") // Basic required validation
                  .matches(RegExp("^(?! )[A-Za-z' -]+(?<! )$"), {
                    message: "Name may only use alphabetic (A-Z) characters and hyphen (-) and apostrophe (')",
                  }),
              },
              {
                type: "field",
                key: "dateOfBirth",
                fieldType: "calendar",
                label: "Child's date of birth",
                props: {
                  isRequired: true,
                  warnType: "warning",
                  warn: () => (
                    <span className="m-0">
                      If the child is over six years old, please ensure that if a school exemption is required that it has been submitted to the DE
                      regional office. This application cannot be assessed until this has occurred.
                    </span>
                  ),
                },
                validation: Yup.date()
                  .required("A response is required")
                  .min(minDob, "Child may not be more than seven (7) years old")
                  .max(maxDob, "Child must be at least two (2) years old"),
              },
              {
                type: "field",
                key: "gender",
                fieldType: "dropdown",
                label: "Gender",
                props: {
                  isRequired: true,
                  placeholder: "Select a Gender",
                  options: [
                    {
                      label: "Female",
                      value: 1,
                    },
                    {
                      label: "Male",
                      value: 2,
                    },
                    {
                      label: "Other",
                      value: 3,
                    },
                  ],
                },
                validation: Yup.string().trim().required("A response is required"),
              },
            ],
          },
          {
            type: "formGroup",
            key: "kindergarten",
            hideExpression: (context, values) => hideSection(values),
            fields: [
              {
                type: "field",
                key: "$type",
                fieldType: "value",
                defaultValue: "kis.logic.DTO.ApplicationValue.KindergartenDTOs.KindergartenCurrentDTO, kis.logic",
              },
              {
                type: "field",
                key: "lGA",
                fieldType: "dropdown",
                label: "LGA the kindergarten is located in",
                props: {
                  isRequired: true,
                  options: LGA,
                  warnType: "warning",
                  warn: () => (
                    <span className="m-0">
                      You can search for the kindergarten's LGA by using the Victorian government's 'Know Your Council' page (link:{" "}
                      <a href="https://www.vic.gov.au/know-your-council" target="_blank" title="Link opens in a new tab" rel="noreferrer">
                        https://www.vic.gov.au/know-your-council
                      </a>
                      <i aria-hidden="true" className="pi pi-external-link ml-1"></i>)
                    </span>
                  ),
                },
                validation: Yup.string().trim().required("A response is required"),
              },
              {
                type: "field",
                key: "fundedKinder",
                fieldType: "search",
                label: "Name of the kindergarten service lodging this application",
                expressionProps: {
                  disabled: (context, values, model) => model.lGA == null,
                  customParams: (context, values, model) => {
                    return { LGA: model.lGA };
                  },
                },

                props: {
                  isRequired: true,
                  type: "warning",
                  Body: () => (
                    <p className="list-none pl-2 m-0 warning">
                      You must record the LGA the kindergarten is located in before completing this field.
                      <br />
                      Enter the first few letters of your kindergarten name to search. If you cannot find the name of the kindergarten in the list,
                      select 'Other' and the "Enter the Name of the Kindergarten" field will display. Enter the name of the kindergarten in that
                      field.
                    </p>
                  ),
                  apiPath: "FundedKinder/search",
                  placeholder: "Kindergarten Name",
                  labelValue: "accountName",
                  noResultsMessage: "The kindergarten service is not listed",
                  displayField: "accountName",
                },
                validation: Yup.object().required("A response is required."),
              },
              {
                type: "field",
                fieldType: "input",
                key: "kindergartenNameOther",
                label: "Please enter the name of the kindergarten service",
                validation: Yup.string().trim().required("A response is required"),
                hideExpression: (context, values, model) => {
                  return model?.fundedKinder?.id !== -1;
                },
              },
              {
                type: "field",
                key: "serviceApprovalNumber",
                fieldType: "input",
                label: "What is the service approval (SE) number of your kindergarten?",
                props: {
                  type: "warning",
                  Body: () => (
                    <div className="service-screening warning">
                      {" "}
                      <ul className="list-none">
                        This is the official service approval number assigned to your kindergarten under the National Quality Framework. It typically
                        begins with
                        <strong> SE-</strong> followed by eight digits (e.g. SE-12345678).
                        <br />
                        You can find this number by searching your service name on the{" "}
                        <a
                          href="https://www.acecqa.gov.au/resources/national-registers/services?s=&f%5B0%5D=service_state%3AVIC"
                          target="_blank"
                          title="Link opens in a new tab"
                          rel="noreferrer">
                          ACECQA National Register
                        </a>
                        .<i aria-hidden="true" className="pi pi-external-link"></i>
                        <br />
                        Alternatively in Arrival:
                        <li>Go to the Services tab</li>
                        <li>Search for the service name</li>
                        <li>The SE number (NQAITS ID) appears in the second column of the search results</li>
                        <li>
                          Alternatively, in the actions columns click the eye icon to open the service record, then under "Service details" the third
                          row should be the SE-number
                        </li>
                      </ul>
                    </div>
                  ),
                },
                validation: Yup.string().notRequired().trim().matches(RegExp("^SE-\\d{8}$"), { message: "A valid SE number is required" }),
              },
              {
                type: "field",
                key: "contactNumber",
                fieldType: "input",
                label: "Phone Number",
                props: {
                  isRequired: true,
                },
                validation: Yup.string()
                  .trim()
                  .required("A response is required")
                  .matches(
                    RegExp(
                      "^((04\\d{2}\\s?\\d{3}\\s?\\d{3})|(\\+614\\s?\\d{4}\\s?\\d{4})|((\\(02\\)|\\(03\\)|\\(08\\))\\s?\\d{4}\\s?\\d{4})|(02|03|08)\\s?\\d{4}\\s?\\d{4}|(\\+613\\s?\\d{4}\\s?\\d{4})|(\\+612\\s?\\d{4}\\s?\\d{4})|(\\+618\\s?\\d{4}\\s?\\d{4}))$"
                    ),
                    {
                      message: "Please enter a valid contact number",
                    }
                  ),
              },
              {
                type: "field",
                key: "advisory21",
                fieldType: "advisory",
                props: {
                  type: "warning",
                },
                label:
                  "Thank you for providing the basic information required to create a new KIS application. Please press submit to create the application.",
              },
              {
                type: "formGroup",
                key: "submitButtonGroup",
                customWrapper: ({ children }: any) => <div className="flex float-left screening-form-btns">{children}</div>,
                fields: [
                  {
                    type: "field",
                    key: "submitButton",
                    fieldType: "button",
                    label: "Submit",
                    hideExpression: (context, values) =>
                      values.applicationBase.screeningQuestions.kinderServiceFunded == 2 ||
                      values.applicationBase.screeningQuestions.fundedKinder?.parentGuardianConsent == 2 ||
                      !values.applicationBase.screeningQuestions.showSubmit ||
                      !(values.applicationBase.screeningQuestions.fundedKinder?.consentYes?.screeningApplicationType == 1),
                    props: {
                      className: "form-button mr-5",
                      type: "submit",
                      onClick: () => {
                        setTimeout(() => {
                          const firstError = document.getElementsByClassName("error")[0] as HTMLElement;
                          firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 200);
                      },
                    },
                  },
                ],
              },
            ],
          },
          {
            type: "field",
            key: "cancelButton1",
            fieldType: "button",
            label: "Cancel ",
            props: {
              className: "cancel-button",
              onClick: () => {
                navigate(-1);
              },
            },
          },
        ],
      },
    ],
  };
  return form;
};

const hideSection = (values: any) =>
  values.applicationBase.screeningQuestions.kinderServiceFunded == 2 ||
  values.applicationBase.screeningQuestions.fundedKinder.parentGuardianConsent == 2 ||
  !values.applicationBase.screeningQuestions.showSubmit ||
  !(values.applicationBase.screeningQuestions.fundedKinder.consentYes.screeningApplicationType == 1);
